import React from 'react';
import { ChecklistType, CHECKLIST_CONFIGS } from '../../types/datamodel';
import { CheckCircle, XCircle } from 'lucide-react';

interface ParteCFormProps {
  data: any;
  checklistType: ChecklistType;
  onChange: (data: any) => void;
}

export function ParteCForm({ data, checklistType, onChange }: ParteCFormProps) {
  const config = CHECKLIST_CONFIGS[checklistType];
  const safeData = data || {};

  const handleChange = (section: string, field: string, value: any) => {
    const currentSection = safeData[section] || {};
    const newData = {
      ...safeData,
      [section]: {
        ...currentSection,
        [field]: value
      }
    };
    onChange(newData);
  };

  const renderEsito = (esito: string) => {
    if (esito === 'CONFORME') {
      return (
        <div className="flex items-center text-green-600 font-semibold">
          <CheckCircle className="w-5 h-5 mr-2" />
          {esito}
        </div>
      );
    }
    if (esito === 'NON CONFORME') {
      return (
        <div className="flex items-center text-red-600 font-semibold">
          <XCircle className="w-5 h-5 mr-2" />
          {esito}
        </div>
      );
    }
    return <span className="text-gray-400">-</span>;
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      {/* C1 - Consensi privacy */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800">C1 - Consensi Privacy</h3>
          {renderEsito(safeData.c1?.esito || '')}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Presenza Firma Privacy <span className="text-red-500">*</span>
            </label>
            <select
              value={safeData.c1?.presenzaFirmaPrivacy || ''}
              onChange={(e) => handleChange('c1', 'presenzaFirmaPrivacy', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Seleziona...</option>
              <option value="SI">SI</option>
              <option value="NO">NO</option>
            </select>
            <p className="mt-2 text-sm text-gray-500">
              Verificare la presenza della firma del contraente sull'informativa privacy
            </p>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
            <textarea
              value={safeData.c1?.note || ''}
              onChange={(e) => handleChange('c1', 'note', e.target.value)}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="Note aggiuntive sui consensi privacy..."
            />
          </div>
        </div>
      </div>

      {/* C2 - Formazione obbligatoria */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800">C2 - Formazione Obbligatoria</h3>
          {renderEsito(safeData.c2?.esito || '')}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Formazione Obbligatoria Completata <span className="text-red-500">*</span>
            </label>
            <select
              value={safeData.c2?.formazioneObbligatoria || ''}
              onChange={(e) => handleChange('c2', 'formazioneObbligatoria', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Seleziona...</option>
              <option value="SI">SI</option>
              <option value="NO">NO</option>
            </select>
            <p className="mt-2 text-sm text-gray-500">
              Verificare che il collocatore abbia completato la formazione obbligatoria
            </p>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
            <textarea
              value={safeData.c2?.note || ''}
              onChange={(e) => handleChange('c2', 'note', e.target.value)}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="Note aggiuntive sulla formazione..."
            />
          </div>
        </div>
      </div>

      {/* C3 - Iscrizione RUI */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800">C3 - Iscrizione RUI</h3>
          {renderEsito(safeData.c3?.esito || '')}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Collocatore Abilitato <span className="text-red-500">*</span>
            </label>
            <select
              value={safeData.c3?.collocatoreAbilitato || ''}
              onChange={(e) => handleChange('c3', 'collocatoreAbilitato', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Seleziona...</option>
              <option value="SI">SI</option>
              <option value="NO">NO</option>
            </select>
            <p className="mt-2 text-sm text-gray-500">
              Il collocatore è abilitato alla vendita?
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Verifica Iscrizione RUI <span className="text-red-500">*</span>
            </label>
            <select
              value={safeData.c3?.verificaIscrizioneRUI || ''}
              onChange={(e) => handleChange('c3', 'verificaIscrizioneRUI', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Seleziona...</option>
              <option value="SI">SI</option>
              <option value="NO">NO</option>
              <option value="NA">NA</option>
            </select>
            <p className="mt-2 text-sm text-gray-500">
              Se abilitato, verificare l'iscrizione al Registro Unico Intermediari
            </p>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
            <textarea
              value={safeData.c3?.note || ''}
              onChange={(e) => handleChange('c3', 'note', e.target.value)}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              placeholder="Note aggiuntive sull'iscrizione RUI..."
            />
          </div>
        </div>

        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-800">
            <strong>RUI:</strong> Il Registro Unico degli Intermediari assicurativi e riassicurativi.
            Verificare sul portale IVASS l'iscrizione del collocatore.
          </p>
        </div>
      </div>

      {/* C4 - Documentazione FATCA (non presente in Motor e P&C) */}
      {config.hasC4 && safeData.c4 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-800">C4 - Documentazione FATCA</h3>
            {renderEsito(safeData.c4?.esito || '')}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Presenza Modulo FATCA <span className="text-red-500">*</span>
              </label>
              <select
                value={safeData.c4?.presenzaModuloFATCA || ''}
                onChange={(e) => handleChange('c4', 'presenzaModuloFATCA', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Seleziona...</option>
                <option value="SI">SI</option>
                <option value="NO">NO</option>
              </select>
              <p className="mt-2 text-sm text-gray-500">
                È presente il modulo FATCA per il contraente?
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Presenza Firma FATCA <span className="text-red-500">*</span>
              </label>
              <select
                value={safeData.c4?.presenzaFirmaFATCA || ''}
                onChange={(e) => handleChange('c4', 'presenzaFirmaFATCA', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Seleziona...</option>
                <option value="SI">SI</option>
                <option value="NO">NO</option>
              </select>
              <p className="mt-2 text-sm text-gray-500">
                Se presente, verificare la firma del contraente
              </p>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
              <textarea
                value={safeData.c4?.note || ''}
                onChange={(e) => handleChange('c4', 'note', e.target.value)}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                placeholder="Note aggiuntive sulla documentazione FATCA..."
              />
            </div>
          </div>

          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <strong>FATCA:</strong> Foreign Account Tax Compliance Act - Normativa USA per la dichiarazione
              di conti finanziari detenuti da cittadini o residenti fiscali statunitensi.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
